import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { NumericInput } from "@/components/ui/numeric-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

export const COMMISSION_STATUS = [
  { value: "waiting_approval", label: "Jóváhagyásra vár", tone: "amber" },
  { value: "approved", label: "Jóváhagyva", tone: "blue" },
  { value: "paid", label: "Kifizetve", tone: "emerald" },
  { value: "rejected", label: "Elutasítva", tone: "rose" },
];

const TONES = {
  emerald:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400",
  blue:
    "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-400",
  amber:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-400",
  rose:
    "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-400",
  muted: "border-border bg-muted text-muted-foreground",
};

const DOTS = {
  emerald: "bg-emerald-500",
  blue: "bg-blue-500",
  amber: "bg-amber-500",
  rose: "bg-rose-500",
  muted: "bg-muted-foreground/60",
};

export function commissionStatusMeta(value) {
  return COMMISSION_STATUS.find((s) => s.value === value) || {
    value,
    label: value || "—",
    tone: "muted",
  };
}

export function CommissionStatusBadge({ value }) {
  const meta = commissionStatusMeta(value);
  return (
    <span
      data-testid={`commission-status-${value}`}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium",
        TONES[meta.tone]
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", DOTS[meta.tone])} />
      {meta.label}
    </span>
  );
}

const numberOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function todayIso() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const emptyForm = {
  amount: "",
  issued_at: "",
  issuer: "",
  reason: "",
};

export function CommissionFormDialog({
  open,
  onOpenChange,
  employee,
  onSaved,
}) {
  const { user } = useAuth();
  const currentUserName = user?.name || user?.email || "—";
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyForm, issuer: currentUserName, issued_at: todayIso() });
    setErr("");
  }, [open, currentUserName]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.amount === "" || !Number.isFinite(Number(form.amount))) {
      setErr("A bruttó összeg megadása kötelező.");
      return;
    }
    if (!form.issued_at) {
      setErr("A kiadás idejének megadása kötelező.");
      return;
    }
    if (!form.issuer.trim()) {
      setErr("A kiadó megadása kötelező.");
      return;
    }
    setSaving(true);
    try {
      if (!employee?._id) throw new Error("Hiányzó munkavállaló.");
      const payload = {
        amount: numberOrNull(form.amount),
        issued_at: form.issued_at,
        issuer: form.issuer.trim(),
        reason: form.reason || null,
      };
      const { data } = await api.post(
        `/employees/${employee._id}/commissions`,
        payload
      );
      toast.success("Jutalék rögzítve.");
      onOpenChange(false);
      if (onSaved) onSaved(data);
    } catch (error) {
      setErr(formatApiError(error?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="commission-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Új jóváírt bruttó jutalék</DialogTitle>
          <DialogDescription>
            Add meg a jutalék részleteit. Az új jutalék „Jóváhagyásra vár”
            státusszal kerül rögzítésre.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Bruttó összeg (Ft) *" testId="commission-amount">
              <NumericInput
                value={form.amount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, amount: e.target.value }))
                }
                placeholder="0"
                className="font-mono"
              />
            </Field>
            <Field label="Kiadás ideje *" testId="commission-issued-at">
              <DateInput
                value={form.issued_at}
                onChange={(v) =>
                  setForm((f) => ({ ...f, issued_at: v }))
                }
              />
            </Field>
          </div>

          <Field label="Kiadó (automatikus)" testId="commission-issuer">
            <div
              data-testid="commission-issuer-display"
              className="flex h-9 items-center rounded-md border border-border bg-muted/40 px-3 text-sm"
            >
              <span className="truncate">{form.issuer || "—"}</span>
            </div>
          </Field>

          <Field label="Indok" testId="commission-reason">
            <Textarea
              value={form.reason}
              onChange={(e) =>
                setForm((f) => ({ ...f, reason: e.target.value }))
              }
              rows={3}
              placeholder="Mire jár a jutalék? (eladás, ügyfél, projekt, stb.)"
            />
          </Field>

          {err && (
            <div
              data-testid="commission-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="commission-cancel"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="commission-submit">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : (
                "Rögzítés"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
